import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/system";

// Full screen dark overlay
const Overlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "#121212",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1300,
  padding: "12px",
});

// Main box for disclaimer content
const DisclaimerContainer = styled(Box)({
  width: "100%",
  maxWidth: "900px",
  height: "75vh",
  backgroundColor: "#1e1e1e",
  borderRadius: "20px",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #333",
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.9)",
  overflow: "hidden",
});

// Scrollable text area
const ContentArea = styled(Box)({
  flex: 1,
  overflow: "auto", // <- FIXED: Use auto instead of scroll
  padding: "20px",
  color: "#e0e0e0",
  fontFamily: "'Roboto', sans-serif",
  fontSize: "13px",
  lineHeight: "1.6",
  textAlign: "justify",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#9f8054",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#333",
  },
});

// Button section
const BottomActions = styled(Box)({
  padding: "16px",
  borderTop: "1px solid #333",
  display: "flex",
  justifyContent: "center",
});

// Fancy gold button
const GoldButton = styled(Button)({
  backgroundColor: "#9f8054",
  color: "#fff",
  fontWeight: "bold",
  fontFamily: "LEMON MILK",
  fontSize: "16px",
  padding: "12px 28px",
  borderRadius: "30px",
  textTransform: "uppercase",
  boxShadow: "0px 8px 24px rgba(159, 128, 84, 0.4)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: "#7f6540",
    transform: "scale(1.05)",
    boxShadow: "0px 12px 32px rgba(159, 128, 84, 0.7)",
  },
});

// Gold divider line
const GoldDivider = styled(Box)({
  width: "100%",
  height: "1px",
  backgroundColor: "#bf742c",
  margin: "10px auto 20px auto",
});

interface DisclaimerPageProps {
  onAccept: () => void;
}

const DisclaimerPage: React.FC<DisclaimerPageProps> = ({ onAccept }) => {
  return (
    <Overlay>
      <DisclaimerContainer>
        <Typography
          variant="h4"
          align="center"
          sx={{
            pt: 3,
            fontWeight: 400,
            color: "#f5f5f5",
            fontFamily: "LEMON MILK",
            fontSize: { xs: "22px", sm: "28px", md: "32px" },
          }}
        >
          Disclaimer
        </Typography>

        <GoldDivider />

        <Typography
          align="center"
          sx={{
            fontWeight: "500",
            color: "#9f8054",
            fontFamily: "LEMON MILK",
            fontSize: { xs: "16px", md: "20px" },
            mb: 1,
          }}
        >
          THANK YOU FOR VISITING OUR WEBSITE.
        </Typography>

        <ContentArea>
        <p>
            The content of this website is for general information purposes only. At Kolla Group, we strive to ensure that the information on this website is up-to-date and accurate. However, visitors are requested to contact an authorized sales representative for more information before relying on the website content. In no event shall Kolla Group offer any warranty on the information made available or be liable for any loss or damage, including, without limitation, indirect or consequential loss or damage, arising in connection with the use of the information on this website.
          </p><br/>
          <p>
            By accessing this site, you unconditionally and without limitation, agree to the terms and conditions outlined in this Disclaimer. The Company reserves the right to change these terms and conditions at its sole discretion without any prior intimation or notification, and you shall be bound by any such changes.
          </p><br/>
          <p>
            Design, specifications, floor plans, brochures, interactive plans, etc., are subject to change without prior notice. Computer-generated images, walkthroughs, and rendered images are the artist's impressions and are indicative of the actual designs. You are required to make your own inquiries, conduct proper due diligence, obtain independent professional advice, and verify the details of the project, including but not limited to area, layout, specifications, services, amenities, and other terms mentioned on this website with the Company, prior to making any decision to purchase any unit in any of the Company's projects.
          </p><br/>
          <p>
            The Company reserves the right to amend or alter the area, layout, specifications, and amenities mentioned on this website due to government norms, advice from the architect/structural engineer, or otherwise. You are also advised to contact the Company's Head Office regarding any clarifications, information, or details concerning each and every project of the Company.
          </p><br/>
          <p>
            The contents of this website shall not be construed as any form of advertisement, solicitation, marketing, offer for sale, assurance, invitation to offer, or contract of any nature whatsoever. It does not give any warranty of any kind—implied, express, or statutory—and shall have no binding effect on the Company. Any decision you make regarding booking based on this website is solely at your own cost and risk.
          </p><br/>
          <p>
            The website and the information contained herein are not intended as legal, financial, or real estate advice and must not be relied upon as such; it is for guidance and general information purposes only. Any information on the website is subject to change without prior notice. Changes to this Disclaimer will be effective immediately upon being posted.
          </p><br/>
          <p>
            Please note that we will not be accepting any bookings or allotments based on the images, materials, stock photography, projections, details, or descriptions currently available and/or displayed on the website. By sharing your contact details on the website, you authorize the Sales Response Team of Kolla Group to provide information about our projects via calls, SMS, and emails.
          </p><br/>
          <p>
            Kolla Group is not responsible for any unauthorized use of the information provided on this website by any third parties or the misuse of its contents.
          </p><br/>
          <p>
            This website and its contents are provided "AS IS," without warranty of any kind, either expressed or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, or non-infringement. Furthermore, the Company does not wish to represent anyone desiring representation based on viewing this website in a state where this website fails to comply with all laws and ethical rules of that state.
          </p><br/>
          <p>
            Reproduction, distribution, republication, and/or retransmission of material contained within the website are prohibited unless prior written permission from the Company has been obtained.
          </p><br/>
          <p>
            The foregoing is subject to the prevailing laws of India, and the courts in Hyderabad, India, shall have exclusive jurisdiction over any dispute that may arise out of the use of this site.
          </p><br/>
          <p>We thank you for your patience and understanding.</p>
        </ContentArea>

        <BottomActions>
          <GoldButton onClick={onAccept}>OK</GoldButton>
        </BottomActions>
      </DisclaimerContainer>
    </Overlay>
  );
};

export default DisclaimerPage;
